import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import './PrivacyPolicy.css';
import tenantStorage from '../../utils/tenantStorage';

const PrivacyPolicy = () => {
  const navigate = useNavigate();
  const [isLoggedIn, setIsLoggedIn] = useState(false);

  // Check login status on component mount
  useEffect(() => {
    const accessToken = tenantStorage.getItem('accessToken');
    setIsLoggedIn(!!accessToken);
  }, []);

  const handleButtonClick = () => {
      navigate(-1);
  };

  return (
    <div className="privacy-container">
      <div className="privacy-header">
        <h1>Privacy Policy</h1>
        <div className="privacy-last-updated">Last updated: August 15, 2025</div>
      </div>
      
      <div className="privacy-content">
        <section className="privacy-section">
          <h2>1. Introduction</h2>
          <p>
            Welcome to RentTick. We are committed to protecting your personal information and your right to privacy. 
            This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you use 
            our hostel management system and related services.
          </p>
          <p>
            Please read this privacy policy carefully. By using RentTick, you agree to the collection and use of 
            information in accordance with this policy.
          </p>
        </section>

        <section className="privacy-section">
          <h2>2. Information We Collect</h2>
          <h3>2.1 Personal Information</h3>
          <p>We collect personal information that you voluntarily provide to us when you:</p>
          <ul>
            <li>Register for an account</li>
            <li>Make a reservation or payment</li>
            <li>Contact us for support or inquiries</li>
            <li>Fill out forms or surveys</li>
          </ul>
          <p>The personal information we collect may include:</p>
          <ul>
            <li>Full name and contact details (email, phone number, address)</li>
            <li>Date of birth and identification documents</li>
            <li>Emergency contact information</li>
            <li>Educational institution details (for student hostels)</li>
            <li>Guardian information (where applicable)</li>
          </ul>
          
          <h3>2.2 Payment Information</h3>
          <p>
            When you make a payment, we collect payment-related information through our secure payment gateway partners. 
            This may include:
          </p>
          <ul>
            <li>Payment card details (processed by our payment gateway providers)</li>
            <li>Billing address</li>
            <li>Transaction history</li>
            <li>UPI IDs or wallet information</li>
          </ul>
          <p>
            <strong>Important:</strong> We do not store complete credit/debit card numbers on our servers. 
            Payment information is securely handled by our PCI DSS compliant payment gateway partners 
            including Razorpay, PayU, PhonePe, and Stripe.
          </p>

          <h3>2.3 Automatically Collected Information</h3>
          <p>When you use our system, we automatically collect certain information, including:</p>
          <ul>
            <li>Device information (type, operating system, browser type)</li>
            <li>IP address and location data</li>
            <li>Login timestamps and activity logs</li>
            <li>System usage patterns and preferences</li>
          </ul>

          <h3>2.4 Communication Information</h3>
          <p>
            To facilitate effective communication, we collect and store information related to our 
            communication with you:
          </p>
          <ul>
            <li>WhatsApp mobile number for automated notifications</li>
            <li>Communication preferences and opt-in/opt-out status</li>
            <li>Message delivery status and read receipts (where available)</li>
            <li>Response to automated messages and inquiries</li>
          </ul>
        </section>

        <section className="privacy-section">
          <h2>3. How We Use Your Information</h2>
          <p>We use the information we collect for the following purposes:</p>
          <ul>
            <li><strong>Service Delivery:</strong> To provide hostel accommodation and manage your account</li>
            <li><strong>Payment Processing:</strong> To process transactions and send payment confirmations</li>
            <li><strong>Communication:</strong> To send important notices, updates, and respond to inquiries</li>
            <li><strong>Facility Management:</strong> To allocate beds, manage check-ins/check-outs, and maintain records</li>
            <li><strong>Safety and Security:</strong> To ensure the safety of residents and prevent fraud</li>
            <li><strong>Improvement:</strong> To analyze system usage and improve our services</li>
            <li><strong>Legal Compliance:</strong> To comply with legal obligations and regulations</li>
          </ul>
        </section>

        <section className="privacy-section">
          <h2>4. WhatsApp Messaging and Communications</h2>
          <h3>4.1 Consent to WhatsApp Messages</h3>
          <p>
            By providing your WhatsApp-enabled mobile number and using RentTick services, you explicitly 
            consent to receive automated WhatsApp messages from us. These messages are essential for 
            effective hostel management and keeping you informed.
          </p>

          <h3>4.2 Types of WhatsApp Messages</h3>
          <p>We send the following types of automated WhatsApp messages:</p>
          <ul>
            <li><strong>Payment Reminders:</strong> Notifications about upcoming payment due dates, overdue payments, and payment confirmations</li>
            <li><strong>Allocation Start Notifications:</strong> Alerts when your bed allocation period begins, including room and bed details</li>
            <li><strong>Allocation End Reminders:</strong> Advance notifications about your allocation expiry and renewal procedures</li>
            <li><strong>Invoice Notifications:</strong> Monthly invoice generation alerts and billing information</li>
            <li><strong>Important Updates:</strong> Critical information about hostel operations, maintenance, or policy changes</li>
            <li><strong>Emergency Alerts:</strong> Urgent notifications regarding safety, security, or facility issues</li>
          </ul>

          <h3>4.3 Message Timing and Frequency</h3>
          <ul>
            <li>Messages are typically sent during reasonable hours (9 AM to 8 PM local time)</li>
            <li>Payment reminders are sent 3-5 days before due dates and on/after due dates for overdue payments</li>
            <li>Allocation notifications are sent at the start and 7 days before the end of allocation periods</li>
            <li>Emergency messages may be sent at any time when necessary</li>
            <li>You can expect 2-5 messages per month under normal circumstances</li>
          </ul>

          <h3>4.4 WhatsApp Data Privacy</h3>
          <p>
            We use WhatsApp Business API for sending messages, which provides end-to-end encryption. 
            Important privacy considerations:
          </p>
          <ul>
            <li>Messages are encrypted during transmission using WhatsApp's security protocols</li>
            <li>We do not access your WhatsApp chat history or personal conversations</li>
            <li>Message templates are pre-approved and contain only necessary information</li>
            <li>Your WhatsApp number is stored securely and not shared with third parties for marketing</li>
            <li>Message delivery status is tracked for service improvement but individual message content is not analyzed</li>
          </ul>

          <h3>4.5 Managing Communication Preferences</h3>
          <ul>
            <li><strong>Essential Messages:</strong> Payment reminders and allocation notifications are mandatory for active residents and cannot be opted out</li>
            <li><strong>Optional Messages:</strong> You may opt-out of non-essential updates by contacting hostel management</li>
            <li><strong>After Checkout:</strong> You will stop receiving messages after your allocation ends and checkout is completed</li>
            <li><strong>Update Contact Info:</strong> You must keep your WhatsApp number updated in your profile</li>
          </ul>

          <h3>4.6 Alternative Communication Channels</h3>
          <p>
            If you prefer not to receive WhatsApp messages, we can communicate through alternative channels:
          </p>
          <ul>
            <li>Email notifications</li>
            <li>SMS text messages</li>
            <li>In-app notifications</li>
            <li>Phone calls for urgent matters</li>
          </ul>
          <p>
            Please note that opting out of WhatsApp does not exempt you from payment obligations or 
            staying informed about critical matters. You must select an alternative communication method.
          </p>
        </section>

        <section className="privacy-section">
          <h2>5. Data Protection and Security</h2>
          <p>
            We implement industry-standard security measures to protect your personal information:
          </p>
          <ul>
            <li><strong>Encryption:</strong> All data transmission is encrypted using SSL/TLS protocols</li>
            <li><strong>Access Control:</strong> Strict access controls limit who can view or modify your data</li>
            <li><strong>Secure Storage:</strong> Data is stored on secure servers with regular backups</li>
            <li><strong>Payment Security:</strong> Payment data is processed through PCI DSS compliant gateways</li>
            <li><strong>Regular Audits:</strong> We conduct regular security audits and updates</li>
          </ul>
          <p>
            While we use reasonable efforts to protect your information, no method of transmission over 
            the internet is 100% secure. We cannot guarantee absolute security but continuously work to 
            improve our security measures.
          </p>
        </section>

        <section className="privacy-section">
          <h2>6. Information Sharing and Disclosure</h2>
          <h3>6.1 We Do Not Sell Your Information</h3>
          <p>
            We do not sell, rent, or trade your personal information to third parties for marketing purposes.
          </p>
          
          <h3>6.2 Service Providers</h3>
          <p>We may share your information with trusted third-party service providers who assist us in:</p>
          <ul>
            <li>Payment processing (Razorpay, PayU, PhonePe, Stripe)</li>
            <li>WhatsApp messaging (WhatsApp Business API)</li>
            <li>SMS and email notifications</li>
            <li>Cloud hosting and data storage</li>
            <li>Analytics and system monitoring</li>
          </ul>
          <p>
            These service providers are contractually obligated to protect your information and use it only 
            for the specific services they provide to us.
          </p>

          <h3>6.3 Legal Requirements</h3>
          <p>We may disclose your information when required by law or to:</p>
          <ul>
            <li>Comply with legal processes or government requests</li>
            <li>Enforce our terms and conditions</li>
            <li>Protect the rights, property, or safety of RentTick, our users, or others</li>
            <li>Prevent fraud or security threats</li>
          </ul>
        </section>

        <section className="privacy-section">
          <h2>7. Data Retention</h2>
          <p>
            We retain your personal information for as long as necessary to provide our services and 
            fulfill the purposes outlined in this privacy policy. Specifically:
          </p>
          <ul>
            <li><strong>Active Residents:</strong> Data is maintained while you are a resident</li>
            <li><strong>Former Residents:</strong> Records are retained for up to 3 years after check-out for legal and accounting purposes</li>
            <li><strong>Payment Records:</strong> Transaction records are maintained as required by tax and financial regulations</li>
            <li><strong>Communication Records:</strong> WhatsApp message logs are retained for 1 year for service improvement</li>
            <li><strong>Account Deletion:</strong> You may request account deletion, subject to legal retention requirements</li>
          </ul>
        </section>

        <section className="privacy-section">
          <h2>8. Your Privacy Rights</h2>
          <p>You have the following rights regarding your personal information:</p>
          <ul>
            <li><strong>Access:</strong> Request a copy of the personal information we hold about you</li>
            <li><strong>Correction:</strong> Request correction of inaccurate or incomplete information</li>
            <li><strong>Deletion:</strong> Request deletion of your personal data (subject to legal requirements)</li>
            <li><strong>Withdrawal:</strong> Withdraw consent for data processing where applicable</li>
            <li><strong>Portability:</strong> Request transfer of your data to another service</li>
            <li><strong>Object:</strong> Object to certain processing activities</li>
          </ul>
          <p>
            To exercise any of these rights, please contact us through the RentTick system or reach out to 
            hostel management. We will respond to your request within 30 days.
          </p>
        </section>

        <section className="privacy-section">
          <h2>9. Cookies and Tracking Technologies</h2>
          <p>
            We use cookies and similar tracking technologies to enhance your experience:
          </p>
          <ul>
            <li><strong>Essential Cookies:</strong> Required for system functionality and login sessions</li>
            <li><strong>Analytics Cookies:</strong> Help us understand how users interact with our system</li>
            <li><strong>Preference Cookies:</strong> Remember your settings and preferences</li>
          </ul>
          <p>
            You can control cookies through your browser settings. However, disabling certain cookies 
            may affect system functionality.
          </p>
        </section>

        <section className="privacy-section">
          <h2>10. Children's Privacy</h2>
          <p>
            Some of our hostel facilities may accommodate minors under the supervision of educational institutions. 
            For residents under 18 years of age:
          </p>
          <ul>
            <li>Guardian consent is required for account creation</li>
            <li>Guardian information must be provided for emergency contacts</li>
            <li>Additional safeguards are in place to protect minor residents</li>
            <li>Guardians have the right to access and manage their child's information</li>
          </ul>
        </section>

        <section className="privacy-section">
          <h2>11. International Data Transfers</h2>
          <p>
            Your information may be transferred to and processed in locations outside your country of residence. 
            We ensure that such transfers comply with applicable data protection laws and that appropriate 
            safeguards are in place to protect your information.
          </p>
        </section>

        <section className="privacy-section">
          <h2>12. Changes to This Privacy Policy</h2>
          <p>
            We may update this privacy policy from time to time to reflect changes in our practices or 
            legal requirements. When we make significant changes:
          </p>
          <ul>
            <li>The "Last Updated" date will be revised</li>
            <li>Residents will be notified via email or system notification</li>
            <li>Important changes will be announced on the notice board</li>
          </ul>
          <p>
            Continued use of RentTick after changes are posted constitutes acceptance of the updated privacy policy.
          </p>
        </section>

        <section className="privacy-section">
          <h2>13. Contact Us</h2>
          <p>
            If you have questions, concerns, or requests regarding this privacy policy or how we handle your 
            personal information, please contact us:
          </p>
          <ul>
            <li><strong>Through the System:</strong> Use the contact form in RentTick</li>
            <li><strong>In Person:</strong> Visit the hostel reception desk</li>
            <li><strong>Contact Details:</strong> Visit our <Link to="/contact" className="privacy-link">Contact Us page</Link> for phone numbers and email addresses</li>
          </ul>
          <p>
            For data protection inquiries, you may also contact the hostel's Data Protection Officer 
            through the channels listed above.
          </p>
        </section>

        <section className="privacy-section">
          <h2>14. Related Policies</h2>
          <p>
            This Privacy Policy should be read in conjunction with our <Link to="/terms" className="privacy-link">Terms and Conditions</Link>, 
            which govern your use of the RentTick system and hostel services.
          </p>
        </section>

        <section className="privacy-section">
          <h2>15. Application Development</h2>
          <p>
            The RentTick hostel management system is developed and maintained by <strong>HeliaTick</strong>. 
            For technical inquiries or system-related privacy concerns, please reach out through the 
            appropriate channels provided within the application.
          </p>
        </section>
      </div>
      
      <div className="privacy-footer">
        <button 
          className="privacy-action-btn"
          onClick={handleButtonClick}
        >
          {'Close'}
        </button>
      </div>
    </div>
  );
};

export default PrivacyPolicy;
